<template>
  <div class="products-page">
    <div class="filter-bar">
      <div class="search-box">
        <select v-model="filter.category_id" class="category-select">
          <option :value="0">全部分类</option>
          <option v-for="cat in categoryOptions" :key="cat.id" :value="cat.id">
            {{ cat.level === 2 ? '└─ ' : '' }}{{ cat.name }}
          </option>
        </select>
        <input 
          type="text" 
          v-model="searchKey" 
          placeholder="搜索物品名称..."
          class="search-input"
        />
        <button @click="search" class="search-btn">搜索</button>
      </div>
      <button @click="showAddModal = true" class="add-btn">+ 添加物品</button>
    </div>

    <div class="table-container">
      <table class="products-table">
        <thead>
          <tr>
            <th>物品ID</th>
            <th>物品图片</th>
            <th>物品名称</th>
            <th>分类</th>
            <th>热门</th>
            <th>已售</th>
            <th>所属店家</th>
            <th>SKU</th>
            <th>状态</th>
            <th>创建时间</th>
            <th>操作</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="product in products" :key="product.id">
            <td>{{ product.id }}</td>
            <td>
              <img :src="product.image_thumb || product.image || DEFAULT_NO_IMAGE" class="product-img" />
            </td>
            <td>{{ product.name }}</td>
            <td>{{ getCategoryName(product.category_id) }}</td>
            <td><span :class="'status ' + (product.is_hot === 1 ? 'hot' : '')">{{ product.is_hot === 1 ? '是' : '否' }}</span></td>
            <td>{{ product.use_count || 0 }}</td>
            <td>{{ product.shop?.community?.name || '-' }} - {{ product.shop?.name || '-' }}</td>
            <td>
              <router-link
                :to="{ name: 'productSku', query: { productId: product.id } }"
                class="sku-link"
              >
                {{ product.sku_count || 0 }}
              </router-link>
            </td>
            <td><span :class="['status', product.status === 1 ? 'is-on' : 'is-off']">{{ product.status === 1 ? '上架' : '下架' }}</span></td>
            <td>{{ formatDate(product.created_at) }}</td>
            <td>
              <button @click="viewDetail(product)" class="action-btn detail">详情</button>
              <button @click="editProduct(product)" class="action-btn edit">编辑</button>
              <button @click="openSkuModal(product)" class="action-btn sku">添加SKU</button>
              <button @click="openSpecModal(product)" class="action-btn spec">关联规格</button>
              <button @click="toggleStatus(product)" class="action-btn" :class="product.status === 1 ? 'disable' : 'enable'">
                {{ product.status === 1 ? '下架' : '上架' }}
              </button>
              <button @click="deleteProduct(product)" class="action-btn delete">删除</button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="pagination">
      <button :disabled="currentPage === 1" @click="currentPage--" class="page-btn">上一页</button>
      <span class="page-info">第 {{ currentPage }} / {{ totalPages }} 页</span>
      <button :disabled="currentPage === totalPages" @click="currentPage++" class="page-btn">下一页</button>
    </div>

    <div v-if="showAddModal" class="modal-overlay" @click.self="closeModal">
      <div class="modal-content">
        <div class="modal-header">
          <h3>{{ editingProduct ? '编辑物品' : '添加物品' }}</h3>
          <button @click="closeModal" class="close-btn">×</button>
        </div>
        <div class="modal-body">
          <div class="form-group">
            <label>* 物品名称</label>
            <input v-model="form.name" type="text" class="form-control" placeholder="请输入物品名称" />
          </div>
          <div class="form-group">
            <label>所属分类</label>
            <select v-model.number="form.category_id" class="form-control">
              <option :value="0">请选择分类</option>
              <option v-for="cat in categoryOptions" :key="cat.id" :value="cat.id">
                {{ cat.level === 2 ? '└─ ' : '' }}{{ cat.name }}
              </option>
            </select>
          </div>
          <div class="form-group">
            <label>所属店家</label>
            <select v-model.number="form.shop_id" class="form-control">
              <option :value="0">请选择店家</option>
              <option v-for="shop in shopOptions" :key="shop.id" :value="shop.id">
                {{ shop.name }}
              </option>
            </select>
          </div>
          <div class="form-group">
            <ImageUpload
              ref="imageUpload"
              :value="form.image || DEFAULT_NO_IMAGE"
              :thumb-value="form.image_thumb"
              :lazy="true"
              category="product"
              label="物品主图片（建议规格：375x375）"
              @input="val => form.image = val"
              @thumb-input="val => form.image_thumb = val"
            />
          </div>
          <div class="form-group">
            <label>是否热门</label>
            <select v-model.number="form.is_hot" class="form-control">
              <option :value="0">否</option>
              <option :value="1">是</option>
            </select>
          </div>
          <div class="form-group">
            <label>排序</label>
            <input v-model.number="form.sort_order" type="number" class="form-control" placeholder="数字越小越靠前" />
          </div>
          <div class="form-group">
            <label>物品描述</label>
            <textarea v-model="form.description" class="form-control" rows="3" placeholder="请输入物品描述"></textarea>
          </div>
          <div class="form-group">
            <label>状态</label>
            <select v-model.number="form.status" class="form-control">
              <option :value="1">上架</option>
              <option :value="0">下架</option>
            </select>
          </div>
        </div>
        <div class="modal-footer">
          <button @click="closeModal" class="btn-cancel">取消</button>
          <button @click="saveProduct" class="btn-save">保存</button>
        </div>
      </div>
    </div>

    <!-- 添加 SKU 弹窗 -->
    <div v-if="showSkuModal" class="modal-overlay" @click.self="closeSkuModal">
      <div class="modal-content">
        <div class="modal-header">
          <h3>为「ID:{{ skuEditingProduct?.id }} - {{ skuEditingProduct?.name }}」添加 SKU</h3>
          <button @click="closeSkuModal" class="close-btn">×</button>
        </div>
        <div class="modal-body">
          <div class="form-group">
            <label>规格（按规格维度逐项选择）</label>
            <SkuSpecSelector :product-id="skuEditingProduct.id" v-model="skuForm.specValueIds" />
          </div>
          <div class="form-group">
            <label>店家自定义货号</label>
            <input v-model="skuForm.code" type="text" class="form-control" placeholder="货号 / 外部编码" />
          </div>
          <div class="form-group">
            <ImageUpload
              ref="skuImageUpload"
              :value="skuForm.image || DEFAULT_NO_IMAGE"
              :lazy="true"
              category="product"
              label="SKU 图片（建议规格：375x375）"
              @input="val => skuForm.image = val"
            />
          </div>
          <div class="form-group">
            <label>计量单位</label>
            <input v-model="skuForm.unit" type="text" class="form-control" placeholder="如：件、个、斤、箱" />
          </div>
          <div class="form-row">
            <div class="form-group form-group-half">
              <label>参考售价（元）</label>
              <input v-model.number="skuSalePriceYuan" type="number" class="form-control" min="0" step="0.01" placeholder="单位为元" />
            </div>
            <div class="form-group form-group-half">
              <label>每单位搬运费（元）</label>
              <input v-model.number="skuCarryFeeYuan" type="number" class="form-control" min="0" step="0.01" placeholder="单位为元" />
            </div>
          </div>
          <div class="form-row">
            <div class="form-group form-group-half">
              <label>步梯每层楼层费（元）</label>
              <input v-model.number="skuFloorFeeYuan" type="number" class="form-control" min="0" step="0.01" placeholder="单位为元" />
            </div>
            <div class="form-group form-group-half">
              <label>累计销量</label>
              <input v-model.number="skuForm.sales_count" type="number" class="form-control" min="0" disabled />
            </div>
          </div>
          <div class="form-row">
            <div class="form-group form-group-half">
              <label>是否默认 SKU</label>
              <select v-model.number="skuForm.is_default" class="form-control">
                <option :value="0">否</option>
                <option :value="1">是</option>
              </select>
            </div>
            <div class="form-group form-group-half">
              <label>状态</label>
              <select v-model.number="skuForm.status" class="form-control">
                <option :value="1">上架</option>
                <option :value="0">下架</option>
              </select>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button @click="closeSkuModal" class="btn-cancel">取消</button>
          <button @click="saveSku" class="btn-save">保存</button>
        </div>
      </div>
    </div>

    <!-- 关联规格弹窗 -->
    <div v-if="showSpecModal" class="modal-overlay" @click.self="closeSpecModal">
      <div class="modal-content">
        <div class="modal-header">
          <h3>为「ID:{{ specEditingProduct?.id }} - {{ specEditingProduct?.name }}」关联规格</h3>
          <button @click="closeSpecModal" class="close-btn">×</button>
        </div>
        <div class="modal-body">
          <div class="form-group">
            <label>已关联规格</label>
            <div v-if="productSpecList.length" class="spec-tags">
              <span v-for="ps in productSpecList" :key="ps.id" class="spec-tag">
                {{ getSpecName(ps.spec_id) }}
                <button class="spec-tag-remove" @click="removeProductSpec(ps)">×</button>
              </span>
            </div>
            <div v-else class="sku-spec-tip sku-spec-empty">尚未关联任何规格</div>
          </div>
          <div class="form-group">
            <label>添加规格</label>
            <select v-model.number="selectedSpecId" class="form-control">
              <option :value="0">请选择规格</option>
              <option v-for="spec in unlinkedSpecOptions" :key="spec.id" :value="spec.id">
                {{ spec.name }}
              </option>
            </select>
          </div>
          <button class="btn-save" :disabled="selectedSpecId === 0" @click="addProductSpec">关联该规格</button>
        </div>
        <div class="modal-footer">
          <button @click="closeSpecModal" class="btn-cancel">关闭</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ImageUpload from '@/components/common/Form/ImageUpload.vue'
import { DEFAULT_NO_IMAGE } from '@/constants'
import { getProductList, createProduct, updateProduct, deleteProduct, createSku } from '@/api/products'
import { getCategoryList } from '@/api/categories'
import { getShopList } from '@/api/shops'
import SkuSpecSelector from '@/components/product/SkuSpecSelector.vue'
import { createSkuSpec, getSpecOptions, getProductSpecs, createProductSpec, deleteProductSpec } from '@/api/specs'

export default {
  name: 'Products',
  components: { SkuSpecSelector, ImageUpload },
  data() {
    return {
      searchKey: '',
      filter: {
        category_id: 0
      },
      currentPage: 1,
      totalPages: 10,
      showAddModal: false,
      editingProduct: null,
      showSkuModal: false,
      skuEditingProduct: null,
      showSpecModal: false,
      specEditingProduct: null,
      productSpecList: [],
      specOptions: [],
      selectedSpecId: 0,
      skuForm: {
        code: '',
        specValueIds: [],
        image: '',
        unit: '',
        sale_price: 0,
        carry_fee: 100,
        floor_fee: 10,
        sales_count: 0,
        status: 1,
        is_default: 0
      },
      form: {
        shop_id: 0,
        name: '',
        category_id: 0,
        image: '',
        image_thumb: '',
        is_hot: 0,
        sort_order: 1,
        description: '',
        status: 1
      },
      products: [],
      categories: [],
      shopOptions: []
    }
  },
  computed: {
    DEFAULT_NO_IMAGE() {
      return DEFAULT_NO_IMAGE
    },
    // 添加 SKU 弹窗以「元」展示/编辑，内部 skuForm 仍以「分」保存（计算与保存单位不变）
    skuSalePriceYuan: {
      get() {
        return this.skuForm.sale_price / 100
      },
      set(val) {
        this.skuForm.sale_price = Math.round(Number(val || 0) * 100)
      }
    },
    skuCarryFeeYuan: {
      get() {
        return this.skuForm.carry_fee / 100
      },
      set(val) {
        this.skuForm.carry_fee = Math.round(Number(val || 0) * 100)
      }
    },
    // 楼层费以「元」展示/编辑，内部 skuForm 仍以「分」保存（计算与保存单位不变）
    skuFloorFeeYuan: {
      get() {
        return this.skuForm.floor_fee / 100
      },
      set(val) {
        this.skuForm.floor_fee = Math.round(Number(val || 0) * 100)
      }
    },
    categoryOptions() {
      const activeCategories = this.categories.filter(cat => cat && cat.status === 1)
      const parentCategories = activeCategories.filter(cat => cat && cat.level === 1).sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
      const result = []
      parentCategories.forEach(parent => {
        if (!parent) return
        result.push(parent)
        const children = activeCategories.filter(cat => cat && cat.parent_id === parent.id).sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
        children.forEach(child => {
          if (!child) return
          result.push(child)
        })
      })
      return result
    },
    unlinkedSpecOptions() {
      const linkedIds = this.productSpecList.map(ps => ps.spec_id)
      return this.specOptions.filter(spec => !linkedIds.includes(spec.id))
    }
  },
  mounted() {
    this.loadCategories()
    this.loadShops()
    this.loadProducts()
  },
  methods: {
    async loadCategories() {
      try {
        const response = await getCategoryList({ page: 1 })
        if (response && response.data) {
          const pageData = response.data
          const items = pageData.data || pageData
          this.categories = Array.isArray(items) ? items.filter(cat => cat && cat.id !== undefined) : []
        }
      } catch (error) {
      }
    },
    async loadShops() {
      try {
        const response = await getShopList({ page: 1 })
        if (response && response.data) {
          const pageData = response.data
          const items = pageData.data || pageData
          this.shopOptions = Array.isArray(items) ? items.filter(m => m && m.id !== undefined) : []
        }
      } catch (error) {
      }
    },
    async loadProducts() {
      try {
        const params = { page: this.currentPage }
        if (this.searchKey.trim()) {
          params.keyword = this.searchKey.trim()
        }
        if (this.filter.category_id > 0) {
          params.category_id = this.filter.category_id
        }
        const response = await getProductList(params)
        if (response && response.data) {
          const pageData = response.data
          const items = pageData.data || pageData
          this.products = Array.isArray(items) ? items.filter(p => p && p.id !== undefined) : []
          this.totalPages = pageData.last_page || 10
        }
      } catch (error) {
      }
    },
    search() {
      this.currentPage = 1
      this.loadProducts()
    },
    getCategoryName(categoryId) {
      if (categoryId === 0) return '未分类'
      const category = this.categories.find(cat => cat && cat.id === categoryId)
      return category ? category.name : '未知分类'
    },
    formatDate(dateStr) {
      if (!dateStr) return '-'
      const date = new Date(dateStr)
      return date.toLocaleString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      })
    },
    viewDetail(product) {
      this.$router.push({ name: 'productDetail', params: { id: product.id } })
    },
    editProduct(product) {
      this.editingProduct = product
      this.form.shop_id = product.shop_id || 0
      this.form.name = product.name
      this.form.category_id = product.category_id || 0
      this.form.image = product.image || ''
      this.form.image_thumb = product.image_thumb || ''
      this.form.is_hot = product.is_hot || 0
      this.form.sort_order = product.sort_order || 1
      this.form.description = product.description || ''
      this.form.status = product.status || 1
      this.showAddModal = true
    },
    async toggleStatus(product) {
      const action = product.status === 1 ? '下架' : '上架'
      if (confirm(`确定要${action}物品 ${product.name} 吗？`)) {
        try {
          const newStatus = product.status === 1 ? 0 : 1
          await updateProduct(product.id, { status: newStatus })
          product.status = newStatus
        } catch (error) {
          alert('更新状态失败')
        }
      }
    },
    async deleteProduct(product) {
      if (confirm(`确定要删除物品 ${product.name} 吗？`)) {
        try {
          await deleteProduct(product.id)
          this.products = this.products.filter(p => p.id !== product.id)
          alert('删除成功')
        } catch (error) {
          alert('删除失败，请稍后重试')
        }
      }
    },
    closeModal() {
      this.showAddModal = false
      this.editingProduct = null
      this.form = {
        shop_id: 0,
        name: '',
        category_id: 0,
        image: '',
        image_thumb: '',
        is_hot: 0,
        sort_order: 1,
        description: '',
        status: 1
      }
    },
    closeSkuModal() {
      this.showSkuModal = false
      this.skuEditingProduct = null
      this.skuForm = {
        code: '',
        specValueIds: [],
        image: '',
        unit: '',
        sale_price: 0,
        carry_fee: 100,
        floor_fee: 10,
        sales_count: 0,
        status: 1,
        is_default: 0
      }
    },
    openSkuModal(product) {
      this.skuEditingProduct = product
      this.skuForm = {
        code: '',
        specValueIds: [],
        image: '',
        unit: '',
        sale_price: 0,
        carry_fee: 100,
        floor_fee: 10,
        sales_count: 0,
        status: 1,
        is_default: 0
      }
      this.showSkuModal = true
    },
    async openSpecModal(product) {
      this.specEditingProduct = product
      this.selectedSpecId = 0
      this.productSpecList = []
      try {
        if (this.specOptions.length === 0) {
          const specRes = await getSpecOptions()
          this.specOptions = (specRes && specRes.data && specRes.data.specs) || []
        }
        await this.loadProductSpecs()
      } catch (error) {
      }
      this.showSpecModal = true
    },
    async loadProductSpecs() {
      if (!this.specEditingProduct) return
      try {
        const res = await getProductSpecs(this.specEditingProduct.id)
        this.productSpecList = (res && res.data && res.data.list) || []
      } catch (error) {
        this.productSpecList = []
      }
    },
    getSpecName(specId) {
      const spec = this.specOptions.find(s => s && s.id === specId)
      return spec ? spec.name : '未知规格'
    },
    async addProductSpec() {
      if (this.selectedSpecId === 0 || !this.specEditingProduct) return
      try {
        await createProductSpec(this.specEditingProduct.id, this.selectedSpecId)
        this.selectedSpecId = 0
        await this.loadProductSpecs()
        alert('关联成功')
      } catch (error) {
        const message = error.response?.data?.message || error.message || '关联失败'
        alert(message)
      }
    },
    async removeProductSpec(ps) {
      if (!confirm(`确定取消关联规格「${this.getSpecName(ps.spec_id)}」吗？`)) return
      try {
        await deleteProductSpec(ps.id)
        await this.loadProductSpecs()
        alert('已取消关联')
      } catch (error) {
        const message = error.response?.data?.message || error.message || '取消关联失败'
        alert(message)
      }
    },
    closeSpecModal() {
      this.showSpecModal = false
      this.specEditingProduct = null
      this.productSpecList = []
      this.selectedSpecId = 0
    },
    async saveSku() {
      try {
        // 懒上传：仅在真正保存时把图片传到服务器（选图只是暂存，不会产生孤儿媒体）
        const uploaded = await this.$refs.skuImageUpload.ensureUploaded()
        if (uploaded) {
          this.skuForm.image = uploaded.url
        }

        const payload = { ...this.skuForm }
        delete payload.specValueIds
        const productId = this.skuEditingProduct.id
        const response = await createSku(productId, payload)
        const newSku = response.data.data || response.data
        if (newSku && newSku.id !== undefined) {
          await Promise.all((this.skuForm.specValueIds || []).map(id => createSkuSpec(newSku.id, id)))
        }
        this.skuEditingProduct.sku_count = (this.skuEditingProduct.sku_count || 0) + 1
        alert('SKU 添加成功')
        this.closeSkuModal()
      } catch (error) {
        const message = error.response?.data?.message || error.message || 'SKU 添加失败'
        alert(message)
      }
    },
    async saveProduct() {
      if (!this.form.name.trim()) {
        alert('请输入物品名称')
        return
      }
      if (this.form.category_id === 0) {
        alert('请选择所属分类')
        return
      }

      try {
        // 懒上传：仅在真正保存时把图片传到服务器（选图只是暂存，不会产生孤儿媒体）
        const uploaded = await this.$refs.imageUpload.ensureUploaded()
        if (uploaded) {
          this.form.image = uploaded.url
          this.form.image_thumb = uploaded.thumb_url
        }

        if (this.editingProduct) {
          await updateProduct(this.editingProduct.id, this.form)
          Object.assign(this.editingProduct, this.form)
          alert('更新成功')
        } else {
          const response = await createProduct(this.form)
          const newProduct = response.data.data || response.data
          if (newProduct && newProduct.id !== undefined) {
            this.products.unshift(newProduct)
          }
          alert('添加成功')
        }
        this.closeModal()
        this.loadProducts()
      } catch (error) {
        const message = error.response?.data?.message || error.message || '保存失败，请稍后重试'
        alert(message)
      }
    }
  }
}
</script>

<style scoped>
.products-page {
  padding: 20px;
}

.filter-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding: 15px 20px;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.06);
}

.search-box {
  display: flex;
  gap: 10px;
  align-items: center;
}

.category-select {
  padding: 10px 15px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  min-width: 150px;
}

.search-input {
  padding: 10px 15px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  width: 250px;
}

.search-btn {
  padding: 10px 20px;
  background: #3498db;
  color: #fff;
  border: none;
  border-radius: 6px;
  cursor: pointer;
}

.add-btn {
  padding: 10px 20px;
  background: #2ecc71;
  color: #fff;
  border: none;
  border-radius: 6px;
  cursor: pointer;
}

.table-container {
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.06);
  overflow: hidden;
}

.products-table {
  width: 100%;
  border-collapse: collapse;
}

.products-table th, .products-table td {
  padding: 12px 14px;
  text-align: left;
  border-bottom: 1px solid #eee;
  font-size: 13px;
}

.products-table th {
  background: #f8f9fa;
  font-weight: 600;
  color: #333;
}

.product-img {
  width: 48px;
  height: 48px;
  object-fit: cover;
  border-radius: 4px;
}

.no-image {
  color: #999;
}

.status {
  padding: 4px 0;
  border-radius: 20px;
  font-size: 12px;
}

.status.is-on { background: #e8f5e9; color: #2ecc71; }
.status.is-off { background: #ffebee; color: #e74c3c; }

.action-btn {
  padding: 6px 12px;
  border: none;
  border-radius: 4px;
  font-size: 12px;
  cursor: pointer;
  margin-right: 5px;
}

.action-btn.edit {
  background: #3498db;
  color: #fff;
}

.action-btn.disable {
  background: #e74c3c;
  color: #fff;
}

.action-btn.enable {
  background: #2ecc71;
  color: #fff;
}

.action-btn.delete {
  background: #95a5a6;
  color: #fff;
}

.action-btn.sku {
  background: #9b59b6;
  color: #fff;
}

.action-btn.detail {
  background: #34495e;
  color: #fff;
}

.action-btn.spec {
  background: #e67e22;
  color: #fff;
}

.spec-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.spec-tag {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  background: #fef0e6;
  color: #e67e22;
  border: 1px solid #f5cba7;
  border-radius: 20px;
  font-size: 13px;
}

.spec-tag-remove {
  border: none;
  background: none;
  color: #e67e22;
  cursor: pointer;
  font-size: 14px;
  line-height: 1;
  padding: 0;
}

.sku-link {
  color: #3498db;
  text-decoration: underline;
  cursor: pointer;
}

.sku-link:hover {
  color: #2980b9;
}

.required {
  color: #e74c3c;
}

.form-row {
  display: flex;
  gap: 15px;
}

.form-group-half {
  flex: 1;
}

.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 20px;
  margin-top: 20px;
}

.page-btn {
  padding: 8px 16px;
  border: 1px solid #ddd;
  border-radius: 4px;
  background: #fff;
  cursor: pointer;
}

.page-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.page-info {
  font-size: 14px;
  color: #666;
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0,0,0,0.5);
  display: flex;
  align-items: center;
  justify-content: center;
}

.modal-content {
  background: #fff;
  border-radius: 8px;
  width: 500px;
  max-width: 90%;
  max-height: 90vh;
  overflow-y: auto;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 20px;
  border-bottom: 1px solid #eee;
}

.modal-header h3 {
  margin: 0;
  font-size: 16px;
}

.close-btn {
  font-size: 24px;
  background: none;
  border: none;
  cursor: pointer;
  color: #999;
}

.modal-body {
  padding: 20px;
}

.form-group {
  margin-bottom: 15px;
}

.form-group label {
  display: block;
  margin-bottom: 8px;
  font-size: 14px;
  color: #333;
}

.form-control {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  box-sizing: border-box;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 15px 20px;
  border-top: 1px solid #eee;
}

.btn-cancel {
  padding: 8px 16px;
  border: 1px solid #ddd;
  border-radius: 4px;
  background: #fff;
  cursor: pointer;
}

.btn-save {
  padding: 8px 16px;
  background: #3498db;
  color: #fff;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}
</style>